import _ from 'lodash'
import { getColorShadeTint } from './colors'
import { getRootVars, setRootVariables, setFontFamily } from './root-var'
import { addClass, removeClass, setAttr, setContent } from './dom';

import { USER_ROLES, USER_ROLES_MAP, PERMISSION_TYPES } from './constant';

// ---------------------------------------------------------------------------
// Duplicate-tab guard.
// Browsers copy sessionStorage into duplicated tabs, which leaks the
// impersonation/switch state. We claim a tabId in localStorage with a live
// heartbeat. If a new tab boots and finds its inherited tabId already claimed
// by an active tab, it is a duplicate -> wipe session-only auth keys so the
// duplicate falls back to the original (localStorage) login.
(function initTabGuard(){
    if (typeof window === 'undefined') return;
    try {
        const SESSION_AUTH_KEYS = ['user', 'access_token', 'userStack', 'user_backup'];
        const CLAIM_PREFIX = '__tabClaim_';
        const STALE_MS = 5000;
        const HEARTBEAT_MS = 2000;

        const newId = () => Date.now() + '-' + Math.random().toString(36).slice(2);

        let tabId = sessionStorage.getItem('__tabId');
        if (tabId) {
            const claim = localStorage.getItem(CLAIM_PREFIX + tabId);
            const fresh = claim && (Date.now() - parseInt(claim, 10)) < STALE_MS;
            if (fresh) {
                // Duplicate tab detected -> drop inherited session auth.
                SESSION_AUTH_KEYS.forEach(k => sessionStorage.removeItem(k));
                tabId = newId();
                sessionStorage.setItem('__tabId', tabId);
            }
        } else {
            tabId = newId();
            sessionStorage.setItem('__tabId', tabId);
        }

        const heartbeat = () => {
            try { localStorage.setItem(CLAIM_PREFIX + tabId, String(Date.now())); } catch (e) {}
        };
        heartbeat();
        setInterval(heartbeat, HEARTBEAT_MS);

        const release = () => {
            try { localStorage.removeItem(CLAIM_PREFIX + tabId); } catch (e) {}
        };
        window.addEventListener('beforeunload', release);
        window.addEventListener('pagehide', release);
    } catch (e) {
        console.log('tab guard init failed', e);
    }
})();

export const updateBodyClass = (choices, value) => {
    // remove class from choices
    if(choices.length > 0) {
        choices.forEach((other) => {
            removeClass('body', other)
        });
    }
    if(_.isArray(value)) {
        _.forEach(value, (val) => {
            addClass('body', val)
        })
    } else {
        addClass('body', value)
    }
}

export const updateHtmlAttr = (value) => {
    // set direction
    setAttr('html', value)
}

export const updateHtmlClass = (choices, value) => {
    // remove class from choices
    if(choices.length > 0) {
        choices.forEach((other) => {
            removeClass('html', other)
        });
    }
    addClass('html', value)
}

export const updateTitle = (value) => {
    // set title
    setContent('[data-setting="app_name"]', value)
}

export const updateColorRootVar = (theme_scheme, theme_color, choices) => {
    const prefix = getRootVars("--prefix") || "bs-";
    let newColors = {};
    let dark = false
    if (theme_scheme !== 'light' && theme_scheme !== 'auto') {
        dark = true
    }

    _.forEach(theme_color.colors, (value, key) => {
        key = key.replace("{{prefix}}", prefix);
        newColors = {
            ...newColors,
            ...getColorShadeTint(key, value, dark),
        };
    });
    setRootVariables(newColors);
    removeClass("body", choices);
    addClass("body", [theme_color.value]);
}

export const updateDomValueBySetting = (setting, Choices) => {
    updateHtmlAttr({prop: 'dir',  value: setting.theme_scheme_direction.value})
    updateThemeScheme(setting.theme_scheme.value, Choices, setting.theme_color)
    updateHtmlClass(Choices.FSChoice, setting.theme_font_size.value)
    updateBodyClass(Choices.StyleAppearanceChoice, setting.theme_style_appearance.value)
    updateBodyClass(Choices.Animation, setting.theme_transition.value)
    updateTitle(setting.app_name.value)
    setFontFamily('body', setting.body_font_family.value)
    setFontFamily('heading', setting.heading_font_family.value)
}

export const updateStorage = (storage,key,value) => {
    if(typeof value !== typeof undefined && typeof key !== typeof undefined) {
        switch (storage) {
            case 'localStorage':
                sessionStorage.removeItem(key)
                localStorage.setItem(key, JSON.stringify(value))       
                break;

            case 'sessionStorage':
                localStorage.removeItem(key)
                sessionStorage.setItem(key, JSON.stringify(value))
                break;
        
            default:
                localStorage.removeItem(key)
                sessionStorage.removeItem(key)
                localStorage.setItem(key, 'none')
                sessionStorage.setItem(key, 'none')
                break;
        }
    }
}

export const getStorage = (key) => {
    if(localStorage.getItem(key) === 'none' || sessionStorage.getItem(key) === 'none') return 'none'
    if((localStorage.getItem(key) !== null && localStorage.getItem(key) !== '' ) || 
    (sessionStorage.getItem(key) !== null && sessionStorage.getItem(key) !== '')) {
        let value = localStorage.getItem(key)
        if(value === null) value = sessionStorage.getItem(key)
        if (value !== null) {
            try {
                return JSON.parse(value);
            } catch (e) {
                return value
            }
        }
    }
    return null
}


export const updateThemeScheme = (value, Choices, theme_color) => {
    let className = []
    if(value === 'auto') {
        className.push('auto')
        if (matchMedia("(prefers-color-scheme: light)").matches) {
            className.push('light')
        } else {
            className.push('dark')
        }
    } else {
        className.push(value)
    }
    const filterValue = className.filter((x) => x !== 'auto')
    updateBodyClass(Choices.SchemeChoice, className)
    updateColorRootVar(...filterValue, theme_color, Choices.ColorChoice)
}

export const storeUser = (data, cb) => {
    try {
        Storage.setItem(
            'access_token',
            data.token
        );
        Storage.setItem(
            'user',
            JSON.stringify(data)
        );
        cb?.();
    } catch(error){
        console.log(error);
    }

}

export const storeUserSync = (data) => {
    try {
        Storage.setItem(
            'user',
            JSON.stringify(data)
        );
    } catch(error){
        console.log(error);
    }

}

export const getUser = () => {
    try {
        const savedUser =
            Storage.getItem('user');
        return savedUser
            ? JSON.parse(savedUser)
            : null;
    } catch(error){
        console.log(error);
        return null;
    }
}


export const getUserAccessToken = () => {
    try {
        return Storage.getItem(
            'access_token'
        );
    } catch(error){
        console.log(error);
    }
}

export function isUserAuthenticated(){

    try {
        const userToken = getUserAccessToken();
        return Boolean(userToken);
    } catch (error) {
        console.log(error);
    }

}

/**
 * List of roles, the given user can access
 */
export const getUserAccessRoles = async() => {

    try {
        
        const user = getUser();
        const userRoles = user.roles_access || user.rolesAccess;
        if(userRoles.length == 0) return null;
        else return userRoles;

    } catch (error) {
        console.log(error);
    }

}

export function getUserReadAccessRoleLabel(){
    
    try {

        const userRoles = getUserAccessRoles();
        const userReadRoles = userRoles[PERMISSION_TYPES.READ].map( val => {
            return { 'value':  val, 'label': USER_ROLES_MAP[val] };
        } );
        return userReadRoles;

    } catch (error) {
        
        console.log(error);
        throw error;
    
    }

}


export function getUserCreateAccessRoleLabel(){
    
    try {

        const userRoles = getUserAccessRoles();
        const userReadRoles = userRoles[PERMISSION_TYPES.CREATE].map( val => {
            return { 'value':  val, 'label': USER_ROLES_MAP[val] };
        } );
        return userReadRoles;

    } catch (error) {
        
        console.log(error);
        throw error;
    
    }

}


export async function getUserUpdateAccessRoleLabel(){
    
    try {
        
        const userRoles = getUserAccessRoles();
        const userReadRoles = userRoles[PERMISSION_TYPES.UPDATE].map( val => {
            return { 'value':  val, 'label': USER_ROLES_MAP[val] };
        } );
        return userReadRoles;

    } catch (error) {
        
        console.log(error);
        throw error;
    
    }

}


export function ifUserAssociateCompany(user_role){

    // return Boolean( [USER_ROLES.RESELLER, USER_ROLES.COMPANY_ADMIN, USER_ROLES.SUB_SUPERADMIN].includes(user_role) );
    return Boolean( [USER_ROLES.COMPANY_ADMIN, USER_ROLES.SUB_SUPERADMIN].includes(user_role) );

}

export function logoutUser(){
    Storage.clearAll();
    return true;
}

export async function switchAs(type, id, name){

    try {
     
        if(type == USER_ROLES.CALL_CENTRE) switchAsCallCentre(id, name);

    } catch (error) {
        
        console.log(`Error while switching account: ${error}`);

    }

}

async function switchAsCallCentre(id,name){
    const savedUser =
        Storage.getItem('user');
    sessionStorage.setItem(
        'user_backup',
        savedUser
    );
    const obj = {
        id,
        username:name,
        role:USER_ROLES.CALL_CENTRE,
        roles_access:{
            [PERMISSION_TYPES.CREATE]:
            [USER_ROLES.CALL_CENTRE_ADMIN],

            [PERMISSION_TYPES.UPDATE]:
            [USER_ROLES.CALL_CENTRE_ADMIN],

            [PERMISSION_TYPES.READ]:
            [USER_ROLES.CALL_CENTRE_ADMIN],

            [PERMISSION_TYPES.DELETE]:
            [USER_ROLES.CALL_CENTRE_ADMIN]
        }
    };
    sessionStorage.setItem(
        'user',
        JSON.stringify(obj)
    );
    return true;
}

export async function switchBack(){
    const savedBackupUser =
        sessionStorage.getItem(
            'user_backup'
        );
    sessionStorage.setItem(
        'user',
        savedBackupUser
    );
    sessionStorage.removeItem(
        'user_backup'
    );
    return true;
}

export async function switchUser(targetUser) {
    try {

        let userStack =
            sessionStorage.getItem('userStack');

        userStack = userStack
            ? JSON.parse(userStack)
            : [];

        let currentUser;
        let currentToken;

        // if already switched, use last stack item
        // otherwise use logged-in user
        if (userStack.length > 0) {

            const lastUser =
                userStack[userStack.length - 1];

            currentUser =
                Storage.getItem('user');

            currentToken =
                Storage.getItem('access_token');

        } else {

            currentUser =
                localStorage.getItem('user');

            currentToken =
                localStorage.getItem(
                    'access_token'
                );
        }

        userStack.push({
            user: currentUser,
            token: currentToken
        });

        sessionStorage.setItem(
            'userStack',
            JSON.stringify(userStack)
        );

        sessionStorage.setItem(
            'user',
            JSON.stringify(targetUser)
        );

        sessionStorage.setItem(
            'access_token',
            targetUser.token
        );

        Storage.multiRemove([
            'loginTimestamp',
            'idleTimestamp'
        ]);

        return true;

    } catch(error){

        console.log(
            'Switch user error:',
            error
        );

        return false;
    }
}

export async function switchBackUser(){
    try{
        let userStack = sessionStorage.getItem('userStack');
        userStack =
            userStack
            ? JSON.parse(userStack)
            :[];

        if(!userStack.length){
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('access_token');
            return false;
        }

        const lastSession = userStack.pop();
        sessionStorage.setItem('user', lastSession.user);
        sessionStorage.setItem('access_token', lastSession.token);
        sessionStorage.setItem('userStack', JSON.stringify(userStack));
        return true;
    } catch(error){
        console.log(error);
        return false;
    }
}

export const Storage = {

    getItem(key){

        const sessionVal =
            sessionStorage.getItem(key);

        if(sessionVal !== null){
            return sessionVal;
        }

        return localStorage.getItem(key);

    },

    setItem(
        key,
        value,
        useSession=false
    ){

        if(useSession){
            sessionStorage.setItem(
                key,
                value
            );
        } else {
            localStorage.setItem(
                key,
                value
            );
        }

    },

    removeItem(key){

        sessionStorage.removeItem(key);
        localStorage.removeItem(key);

    },

    multiRemove(keys){

        keys.forEach(key=>{

            sessionStorage.removeItem(key);
            localStorage.removeItem(key);

        });

    },

    clearAll(){

        sessionStorage.clear();
        localStorage.clear();

    },

    multiGet(keys){

        return keys.map(key => {

            const value = this.getItem(key);
            return [
                key,
                value
            ];

        });

    },
}